import { WORLD_H, WORLD_W } from '../constants.js';
import type { Rng } from '../rng.js';
import type { Prop, PropKind, WaterRegion } from '../types.js';
import type { Theme } from '../data/themes.js';
import { surfaceY } from './mask.js';

export interface PropTemplate {
  kind: PropKind;
  w: number;
  h: number;
  hp: number;
}

/** Per-kind body sizes — stamped by the sim, reviewed by the showcase. */
export const TEMPLATES: Record<PropKind, PropTemplate> = {
  tree: { kind: 'tree', w: 44, h: 130, hp: 30 },
  rock: { kind: 'rock', w: 46, h: 34, hp: 60 },
  flower: { kind: 'flower', w: 16, h: 30, hp: 5 },
  pot: { kind: 'pot', w: 30, h: 34, hp: 15 },
  bush: { kind: 'bush', w: 44, h: 36, hp: 20 },
  lamp: { kind: 'lamp', w: 10, h: 46, hp: 10 },
  crystal: { kind: 'crystal', w: 22, h: 34, hp: 25 },
};

let nextPropId = 1;

export function resetPropIds(start = 1): void {
  nextPropId = start;
}

function inWater(water: WaterRegion[], x: number): boolean {
  return water.some((w) => x >= w.x - 10 && x <= w.x + w.w + 10);
}

/**
 * Scatter nature props along the surface. Avoids spawn positions and water.
 * Column-stepped so density is tunable per theme.
 */
export function placeProps(
  terrain: Uint8Array,
  theme: Theme,
  rng: Rng,
  avoid: { x: number; y: number }[],
  water: WaterRegion[],
): Prop[] {
  const props: Prop[] = [];
  const STEP = 26;
  let id = nextPropId;

  for (let x = 30; x < WORLD_W - 30 && props.length < 190; x += STEP) {
    const sy = surfaceY(terrain, x);
    if (sy < 0 || sy > WORLD_H - 40) continue;
    if (inWater(water, x)) continue;
    if (avoid.some((a) => Math.abs(a.x - x) < 130)) continue;
    // Don't hang props off steep cliffs.
    const syL = surfaceY(terrain, x - 18);
    const syR = surfaceY(terrain, x + 18);
    if (syL < 0 || syR < 0 || Math.abs(syL - sy) > 26 || Math.abs(syR - sy) > 26) continue;

    const roll = rng.next();
    let kind: PropKind | null = null;
    const lamp = theme.lampDensity ?? 0;
    const crystal = theme.crystalDensity ?? 0;
    if (roll < theme.treeDensity) kind = 'tree';
    else if (roll < theme.treeDensity + theme.rockDensity) kind = 'rock';
    else if (roll < theme.treeDensity + theme.rockDensity + theme.flowerDensity) kind = 'flower';
    else if (roll < theme.treeDensity + theme.rockDensity + theme.flowerDensity + theme.bushDensity)
      kind = 'bush';
    else if (roll < theme.treeDensity + theme.rockDensity + theme.flowerDensity + theme.bushDensity + theme.potDensity)
      kind = 'pot';
    else if (roll < theme.treeDensity + theme.rockDensity + theme.flowerDensity + theme.bushDensity + theme.potDensity + lamp)
      kind = 'lamp';
    else if (roll < theme.treeDensity + theme.rockDensity + theme.flowerDensity + theme.bushDensity + theme.potDensity + lamp + crystal)
      kind = 'crystal';
    if (!kind) continue;

    // Small clustered flowers: roll twice for them so they grow in tufts.
    const tpl = TEMPLATES[kind];
    const sizeVar = kind === 'tree' ? rng.range(0.8, 1.35) : rng.range(0.85, 1.2);
    const h = Math.round(tpl.h * sizeVar);
    const w = Math.round(tpl.w * (kind === 'tree' ? Math.min(sizeVar, 1.15) : sizeVar));
    props.push({
      id: id++,
      kind,
      x: Math.round(x + rng.range(-6, 6)),
      y: sy,
      w,
      h,
      hp: tpl.hp,
      maxHp: tpl.hp,
      alive: true,
      seed: rng.int(0, 1 << 30),
      style: rng.next(),
    });
  }

  nextPropId = id;
  return props;
}
